import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, ArrowDownCircle, ArrowUpCircle, Building2,
  Users, CalendarClock, PiggyBank, BarChart3, LogOut, FileCheck2, Landmark,
  ShieldCheck, ListChecks, CreditCard, FileText, Settings, Calculator,
  BookOpen, UserCog, Truck, Boxes, ChevronDown, Briefcase, Wallet, Cog, FileUp,
  Menu, X, ShoppingCart, HelpCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV_GROUPS = [
  { roman: "I", label: "Panel", icon: LayoutDashboard, to: "/dashboard", tid: "nav-dashboard", color: "indigo" },
  { roman: "II", label: "Yapılacaklar", icon: ListChecks, to: "/yapilacaklar", tid: "nav-todos", color: "amber" },
  {
    roman: "III", label: "Cari Modülü", icon: Users, tid: "nav-group-cari", color: "emerald",
    children: [
      { to: "/gelirler", label: "Gelirler", icon: ArrowDownCircle, tid: "nav-incomes" },
      { to: "/giderler", label: "Giderler", icon: ArrowUpCircle, tid: "nav-expenses" },
      { to: "/faturalar", label: "Faturalar", icon: FileText, tid: "nav-invoices" },
      { to: "/fatura-ice-aktar", label: "Fatura İçe Aktar", icon: FileUp, tid: "nav-invoice-imports" },
      { to: "/cari", label: "Cari Hesaplar", icon: Users, tid: "nav-contacts" },
      { to: "/hesaplar", label: "Hesaplar", icon: Building2, tid: "nav-accounts" },
      { to: "/yaklasan", label: "Yaklaşan", icon: CalendarClock, tid: "nav-upcoming" },
    ],
  },
  { roman: "IV", label: "Stoklar", icon: Boxes, to: "/stok", tid: "nav-stocks", color: "orange" },
  {
    roman: "V", label: "Banka", icon: Wallet, tid: "nav-group-banka", color: "sky",
    children: [
      { to: "/cekler", label: "Çekler", icon: FileCheck2, tid: "nav-cheques" },
      { to: "/cek-kocanlari", label: "Çek Koçanları", icon: BookOpen, tid: "nav-cheque-books" },
      { to: "/krediler", label: "Krediler", icon: Landmark, tid: "nav-loans" },
      { to: "/kredi-kartlari", label: "Kredi Kartları", icon: CreditCard, tid: "nav-cards" },
      { to: "/teminat-mektuplari", label: "Teminat Mektupları", icon: ShieldCheck, tid: "nav-guarantees" },
    ],
  },
  {
    roman: "VI", label: "Müşteriler", icon: Briefcase, tid: "nav-group-musteri", color: "violet",
    children: [
      { to: "/fiyatlandirma", label: "Fiyat Hesaplama", icon: Calculator, tid: "nav-pricing" },
      { to: "/teklifler", label: "Fiyat Teklifleri", icon: FileText, tid: "nav-quotes" },
      { to: "/tedarikci-siparisleri", label: "Tedarikçi Siparişleri", icon: ShoppingCart, tid: "nav-supplier-orders" },
      { to: "/nakliye", label: "Nakliye", icon: Truck, tid: "nav-shipping" },
    ],
  },
  { roman: "VII", label: "Personel", icon: UserCog, to: "/personel", tid: "nav-employees", color: "pink" },
  {
    roman: "VIII", label: "Ayarlar ve Raporlar", icon: Cog, tid: "nav-group-ayarlar", color: "teal",
    children: [
      { to: "/ayarlar", label: "Ayarlar", icon: Settings, tid: "nav-settings" },
      { to: "/butceler", label: "Bütçeler", icon: PiggyBank, tid: "nav-budgets" },
      { to: "/raporlar", label: "Raporlar", icon: BarChart3, tid: "nav-reports" },
      { to: "/dokuman", label: "Kullanıcı Dokümanı", icon: HelpCircle, tid: "nav-docs" },
    ],
  },
];

const COLOR_MAP = {
  indigo:  { chip: "bg-indigo-100 text-indigo-700",   icon: "text-indigo-600",  bar: "border-indigo-400",  hover: "hover:bg-indigo-50" },
  amber:   { chip: "bg-amber-100 text-amber-800",     icon: "text-amber-600",   bar: "border-amber-400",   hover: "hover:bg-amber-50" },
  emerald: { chip: "bg-emerald-100 text-emerald-700", icon: "text-emerald-600", bar: "border-emerald-400", hover: "hover:bg-emerald-50" },
  orange:  { chip: "bg-orange-100 text-orange-700",   icon: "text-orange-600",  bar: "border-orange-400",  hover: "hover:bg-orange-50" },
  sky:     { chip: "bg-sky-100 text-sky-700",         icon: "text-sky-600",     bar: "border-sky-400",     hover: "hover:bg-sky-50" },
  violet:  { chip: "bg-violet-100 text-violet-700",   icon: "text-violet-600",  bar: "border-violet-400",  hover: "hover:bg-violet-50" },
  pink:    { chip: "bg-pink-100 text-pink-700",       icon: "text-pink-600",    bar: "border-pink-400",    hover: "hover:bg-pink-50" },
  teal:    { chip: "bg-teal-100 text-teal-700",       icon: "text-teal-600",    bar: "border-teal-400",    hover: "hover:bg-teal-50" },
};
const cx = (c) => COLOR_MAP[c] || COLOR_MAP.indigo;

const FLAT_NAV = NAV_GROUPS.flatMap((g) => g.children ? g.children : [g]);

// pricing_only rolündeki kullanıcı için sadece bu rota gösterilir
const PRICING_ONLY_PATHS = new Set(["/fiyatlandirma"]);

function filterNavForRole(role) {
  if (role !== "pricing_only") return NAV_GROUPS;
  // pricing_only: sadece "Fiyat Hesaplama"yı standalone tepe-seviye link olarak göster.
  // "Müşteriler" grubu görünmez → başlık maskelenmiş olur.
  const found = NAV_GROUPS.flatMap((g) => g.children || []).find(
    (c) => PRICING_ONLY_PATHS.has(c.to)
  );
  if (!found) return [];
  return [{
    roman: "I",
    label: found.label,
    icon: found.icon,
    to: found.to,
    tid: found.tid,
    color: "violet",
  }];
}


/** Nav içeriği — hem desktop sidebar hem mobile drawer paylaşır.
 * `onNavigate` prop'u bir alt link tıklanınca (drawer'ı kapatmak için) çağrılır. */
function NavContent({ openGroups, toggleGroup, onNavigate, groups }) {
  return (
    <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
      {groups.map((g) => {
        if (g.to) {
          const gc = cx(g.color);
          return (
            <NavLink
              key={g.label}
              to={g.to}
              onClick={onNavigate}
              data-testid={g.tid}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-black text-white" : `text-neutral-700 ${gc.hover}`
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`w-6 shrink-0 text-[10px] font-mono-num font-bold uppercase tracking-widest text-center py-0.5 ${
                    isActive ? "bg-white/15 text-white" : gc.chip
                  }`}>
                    {g.roman}
                  </span>
                  <g.icon size={15} strokeWidth={1.7} className={isActive ? "" : gc.icon} />
                  <span className="truncate">{g.label}</span>
                </>
              )}
            </NavLink>
          );
        }
        const isOpen = openGroups.has(g.label);
        const hasActive = g.children?.some((c) => window.location.pathname.startsWith(c.to));
        const gc = cx(g.color);
        return (
          <div key={g.label} className="select-none">
            <button
              type="button"
              onClick={() => toggleGroup(g.label)}
              data-testid={g.tid}
              aria-expanded={isOpen}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${
                hasActive ? "bg-neutral-900 text-white" : `text-neutral-800 ${gc.hover}`
              }`}
            >
              <span className={`w-6 shrink-0 text-[10px] font-mono-num font-bold uppercase tracking-widest text-center py-0.5 ${
                hasActive ? "bg-white/15 text-white" : gc.chip
              }`}>
                {g.roman}
              </span>
              <g.icon size={15} strokeWidth={1.7} className={hasActive ? "" : gc.icon} />
              <span className="flex-1 text-left truncate font-medium">{g.label}</span>
              <ChevronDown
                size={13}
                strokeWidth={2}
                className={`transition-transform ${isOpen ? "rotate-180" : ""} ${hasActive ? "" : gc.icon}`}
              />
            </button>
            {isOpen && (
              <div className={`ml-6 my-0.5 border-l-2 ${gc.bar} pl-2 space-y-0.5`} data-testid={`${g.tid}-children`}>
                {g.children.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    onClick={onNavigate}
                    data-testid={c.tid}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-[13px] transition-colors ${
                        isActive
                          ? "bg-black text-white"
                          : `text-neutral-600 hover:text-neutral-900 ${gc.hover}`
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <c.icon size={13} strokeWidth={1.7} className={isActive ? "" : gc.icon} />
                        <span className="truncate">{c.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}


/** Kullanıcı kartı (sidebar/drawer alt bandı) */
function UserFooter({ user, onLogout }) {
  return (
    <div className="border-t border-black/10 p-4">
      <div className="flex items-center gap-3">
        {user?.picture
          ? <img src={user.picture} alt="" className="w-9 h-9 border border-black/20" />
          : <div className="w-9 h-9 bg-neutral-200" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-[11px] text-neutral-500 truncate">{user?.email}</div>
        </div>
        <button
          data-testid="logout-button"
          onClick={onLogout}
          className="p-2 hover:bg-neutral-100 border border-transparent hover:border-black/10"
          title="Çıkış"
        >
          <LogOut size={15} />
        </button>
      </div>
    </div>
  );
}


export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navGroups = useMemo(() => filterNavForRole(user?.role), [user?.role]);

  const activeGroupLabel = useMemo(() => {
    for (const g of navGroups) {
      if (g.children?.some((c) => location.pathname.startsWith(c.to))) return g.label;
    }
    return null;
  }, [location.pathname, navGroups]);

  const [openGroups, setOpenGroups] = useState(() => new Set(activeGroupLabel ? [activeGroupLabel] : []));
  useEffect(() => {
    if (activeGroupLabel) {
      setOpenGroups((prev) => {
        if (prev.has(activeGroupLabel)) return prev;
        const next = new Set(prev);
        next.add(activeGroupLabel);
        return next;
      });
    }
  }, [activeGroupLabel]);

  const toggleGroup = (label) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Mobil drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  // Rota değişince drawer'ı kapat (NavLink onClick zaten kapatıyor ama emniyet için)
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Drawer açıkken Escape ile kapat, body scroll'unu kilitle
  useEffect(() => {
    if (!drawerOpen) return;
    const onEsc = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    document.addEventListener("keydown", onEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);

  return (
    <div className="min-h-screen grain flex bg-[hsl(var(--background))]">
      {/* ==== DESKTOP SIDEBAR ==== */}
      <aside className="w-[240px] hidden md:flex flex-col border-r border-black/10 bg-white sticky top-0 h-screen">
        <div className="p-6 border-b border-black/10 flex items-center gap-3">
          <img src="/plasper-logo.png" alt="Plasper" className="w-9 h-9 object-contain flex-shrink-0" />
          <div className="leading-tight min-w-0">
            <div className="font-display font-bold tracking-tight text-base truncate">Plasper Finans</div>
            <div className="text-[9px] text-neutral-500 uppercase tracking-[0.2em]">Ambalaj</div>
          </div>
        </div>
        <NavContent openGroups={openGroups} toggleGroup={toggleGroup} onNavigate={undefined} groups={navGroups} />
        <UserFooter user={user} onLogout={logout} />
      </aside>

      <main className="flex-1 min-w-0 relative z-[2]">
        {/* ==== MOBILE TOP BAR ==== */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-black/10 bg-white sticky top-0 z-30">
          <button
            data-testid="mobile-nav-toggle"
            onClick={() => setDrawerOpen(true)}
            aria-label="Menüyü aç"
            className="p-2 -ml-2 hover:bg-neutral-100 border border-transparent hover:border-black/10"
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2">
            <img src="/plasper-logo.png" alt="Plasper" className="w-7 h-7 object-contain" />
            <span className="font-display font-bold">Plasper Finans</span>
          </div>
          {/* Sağ tarafa denge — profil avatarı */}
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 border border-black/20" />
          ) : (
            <div className="w-8 h-8 bg-neutral-200 border border-black/20" />
          )}
        </div>

        <div className="p-4 md:p-10">{children}</div>
      </main>

      {/* ==== MOBILE DRAWER + BACKDROP ==== */}
      {drawerOpen && (
        <>
          <div
            onClick={closeDrawer}
            className="md:hidden fixed inset-0 z-40 bg-black/50 animate-fade-in"
            data-testid="mobile-nav-backdrop"
            aria-hidden="true"
          />
          <aside
            className="md:hidden fixed left-0 top-0 bottom-0 z-50 w-[85vw] max-w-[300px] bg-white flex flex-col border-r-2 border-black shadow-[8px_0_0_0_rgba(0,0,0,0.9)] animate-slide-in-left"
            data-testid="mobile-nav-drawer"
            role="dialog"
            aria-label="Menü"
          >
            <div className="p-4 border-b-2 border-black flex items-center gap-3">
              <img src="/plasper-logo.png" alt="Plasper" className="w-9 h-9 object-contain flex-shrink-0" />
              <div className="leading-tight min-w-0 flex-1">
                <div className="font-display font-bold tracking-tight text-base truncate">Plasper Finans</div>
                <div className="text-[9px] text-neutral-500 uppercase tracking-[0.2em]">Ambalaj</div>
              </div>
              <button
                data-testid="mobile-nav-close"
                onClick={closeDrawer}
                aria-label="Menüyü kapat"
                className="p-1.5 hover:bg-neutral-100"
              >
                <X size={18} />
              </button>
            </div>
            <NavContent openGroups={openGroups} toggleGroup={toggleGroup} onNavigate={closeDrawer} groups={navGroups} />
            <UserFooter user={user} onLogout={() => { closeDrawer(); logout(); }} />
          </aside>
        </>
      )}

      <style>{`
        @keyframes slide-in-left {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-slide-in-left { animation: slide-in-left 220ms cubic-bezier(0.32, 0.72, 0, 1); }
        .animate-fade-in { animation: fade-in 180ms ease-out; }
      `}</style>
    </div>
  );
}

export { FLAT_NAV };
